/* GET and POST: api/email/... */
import axios from "axios";
import { backend_url } from "@/constants";
import { Endpoints } from "@api/endpoints";
import { AXIOS_CONFIG } from "@utils/api";
import { pollJob } from "@api/jobApi";
import type {
  GenerateEmailPayload,
  GenerateEmailResponse,
  SendEmailPayload,
  Template,
} from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import { SmartwizError } from "@utils/errors.ts";

/**
 *
 * a helper function to get and post email data
 */
export const getEmailData = async (userId: string[] | string) => {
  try {
    const response = await axios.get(`${backend_url}${Endpoints.email.email}`, {
      params: {
        clientId: userId,
      },
      ...AXIOS_CONFIG,
    });
    if (response?.data?.content) {
      // Sort emails by sendDate in descending order
      const sortedEmails = response.data.content.sort((a: any, b: any) => {
        const dateA = new Date(a.sendDate);
        const dateB = new Date(b.sendDate);
        return dateB.getTime() - dateA.getTime();
      });
      localStorage.setItem("emails", JSON.stringify(sortedEmails));
    } else {
      console.error("No content found in response data");
    }
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 *
 * @param content a JSON object
 * @returns response after sending email
 */
export const sendEmailData = async (content: SendEmailPayload) => {
  const response = await axios.post(
    `${backend_url}${Endpoints.email.email}`,
    content,
    {
      ...AXIOS_CONFIG,
    }
  );
  if (response?.status !== 200) {
    throw new SmartwizError({ message: "Error sending email" });
  }
  const jobId = response.data.content.id;
  const jobStatus = await pollJob(jobId);
  if (jobStatus === "failed") {
    throw new SmartwizError({
      message:
        "An error occurred while sending email. Please try again in a few minutes.",
    });
  }
  return jobStatus === "completed";
};

/**
 * Generates an email subject and body.
 * @param payload an object
 * @returns response after generating email with OpenAI
 */
export const generateEmailOpenAi = async (
  payload: GenerateEmailPayload
): Promise<GenerateEmailResponse> => {
  const response = await axios.post(
    `${backend_url}${Endpoints.email.openAi}`,
    payload,
    {
      ...AXIOS_CONFIG,
    }
  );
  return response.data.content.email as GenerateEmailResponse;
};

/**
 * Sends API request to create new template
 * @param content Payload to request.
 */
export const createEmailTemplate = async (content: object) => {
  const response = await axios.post(
    `${backend_url}/${Endpoints.emailTemplates}`,
    content,
    {
      ...AXIOS_CONFIG,
    }
  );
  return response.data;
};

/**
 * a helper functions related to email templates
 */

export const getEmailTemplates = async (): Promise<Template[]> => {
  try {
    const response = await axios.get(`${backend_url}/api/email/templates/`, {
      ...AXIOS_CONFIG,
    });

    if (!response?.data?.content) {
      console.error("Invalid API response format:", response.data);
      return [];
    }

    // Process the templates to add isDefault
    return response.data.content.map((template: any) => {
      // Ensure template ID is a string
      const id = String(template.id);

      // Consider a template as default if ID starts with 'd'
      const isDefault = id.startsWith("d");

      return {
        ...template,
        id, // Ensure ID is a string
        isDefault,
      };
    });
  } catch (error) {
    console.error("Error fetching email templates:", error);
    return [];
  }
};

// Helper function to delete an email template
export const deleteEmailTemplate = async (templateId: string) => {
  const response = await axios.delete(
    `${backend_url}/api/email/templates/${templateId}/`,
    {
      ...AXIOS_CONFIG,
    }
  );
  return response.data;
};

/**
 * Updates template properties.
 * @param template The template to update.
 */
export const updateEmailTemplate = async (
  template: Template
): Promise<Template> => {
  if (!template.id) {
    throw new Error("Template ID is required to update template.");
  }

  // Don't allow updating default templates
  if (template.isDefault) {
    throw new Error("Default templates cannot be updated directly.");
  }

  const response = await axios.put(
    `${backend_url}/${Endpoints.emailTemplates}${template.id}/`,
    template,
    {
      ...AXIOS_CONFIG,
    }
  );
  return response.data.content.template;
};
